import pluralize from "pluralize";
import {CacheLength} from "../util/cacheLength";
import {log} from "@/lib/Logger";
import {makeLoggableError} from "@aops-trove/fast-server-common";

export const STRAPI_URL =
	process.env.NEXT_PUBLIC_STRAPI_API_URL ||
	process.env.STRAPI_API_URL ||
	"http://localhost:13025";

const STRAPI_API_URL = `${STRAPI_URL}/api`;
export const STRAPI_CACHE_TAG_PREFIX = "strapi";

/** Type of an error as returned from strapi api */
export type StrapiError = {
	data: null;
	error: {
		status: number;
		name: string;
		message: string;
		details: object;
	};
};

/** Type of a strapi collection api result */
export type StrapiSuccessCollection = {
	data: object[];
	meta: {
		pagination: {
			page: number;
			pageSize: number;
			pageCount: number;
			total: number;
		};
	};
};

/** Type of a strapi singular type api result */
export type StrapiSuccessSingle = {
	data: object;
	meta: {};
};

/** Type of any successful strapi api result */
type StrapiSuccess = StrapiSuccessCollection | StrapiSuccessSingle;

/** Type of a strapi api result */
type StrapiReturn = StrapiSuccess | StrapiError | undefined;

/** When we know for certain that we're fetching a collection */
export type StrapiCollectionReturn =
	| StrapiSuccessCollection
	| StrapiError
	| undefined;

export type StrapiQueryParams = Record<string, string>;

export interface FetchFromStrapiOptions extends RequestInit {
	deep?: boolean;
	params?: Record<string, string>;
	fallback?: any;
	apiType?: string;
}

interface RequestFromStrapiApiOptions extends FetchFromStrapiOptions {
	/** Page to query. Defaults to page 1, since pages are 1-indexed */
	page?: number;
}

interface FetchRemainingPagesFromStrapiOptions extends FetchFromStrapiOptions {
	/** First page's collection results to add further results to */
	firstPage: StrapiSuccessCollection;
}

/**
 * Takes a cache tag name and formats it to match the strapi cache tag style.
 * This is a function to ensure consistency in formatting of tags across multiple creations
 */
export function formatStrapiCacheTag(tag: string) {
	return `${STRAPI_CACHE_TAG_PREFIX} ${tag}`;
}

/**
 * Generates a tag for cache-breaking a specific strapi element group.
 * Uses pluralize to try and guess the singular form of the word to match strapi's types
 * @param path Assumed plural of the Strapi internal id for a type. This is known in our codebase as any of the following names:
 *  - Path
 *  - API ID
 *  - Model
 * @returns cache-breaking tag for this specific strapi type
 */
export function generateSingularCacheTag(path: string) {
	// Convert any plural names to singular names, as strapi uses singular internally
	// Strapi uses the same pluralize library so this should always match
	// Ex: campuses => campus
	const modelId = pluralize(path, 1);

	const formattedTag = formatStrapiCacheTag(modelId);

	return formattedTag;
}

/**
 * Set up multiple cache tags for fetches
 */
function generateAllCacheTags(path: string, apiType?: string) {
	const cacheTags = [STRAPI_CACHE_TAG_PREFIX];

	// Remove any slashes from the path
	const cleanPath = path.replaceAll(/\\|\//g, "");

	// Generate tag from path with no transformations done to it. Good for singular data types
	const pathDirectTag = formatStrapiCacheTag(cleanPath);
	cacheTags.push(pathDirectTag);

	// Generate tag from path with it converted to singular. Good for most collection data types
	const pathCacheTag = generateSingularCacheTag(cleanPath);
	cacheTags.push(pathCacheTag);

	// Generate tag from a custom input. Good for data types with custom pluralization going on
	if (apiType) {
		const apiTypeCacheTag = formatStrapiCacheTag(apiType);
		cacheTags.push(apiTypeCacheTag);
	}

	return cacheTags;
}

/**
 * Makes a single fetch request to the strapi api.
 * This is NOT interchangeable with `fetchFromStrapi` and should not be used outside of this file
 * @throws Error if strapi throws a 404
 */
const _singleStrapiFetch = async (
	path: string,
	{
		page = 1,
		deep = true,
		params = {},
		apiType,
		...options
	}: RequestFromStrapiApiOptions = {}
) => {
	const slash = path.startsWith("/") ? "" : "/";
	/**
	 * TODO: param parsing for array values (i.e. generating query strings like
	 * filter[0]=foo&filter[1]=bar should happen here.)
	 */
	const urlParams = new URLSearchParams(params);
	if (deep) {
		urlParams.set("populate", "deep");
	}

	// Set the page to search for
	// If a strapi type is a singular type, this param is ignored
	urlParams.set("pagination[page]", `${page}`);

	const cacheTags = generateAllCacheTags(path, apiType);

	const url = `${STRAPI_API_URL}${slash}${path}?${urlParams.toString()}`;

	// (Used to be retry functionality here, has been removed after seeing load test issues)

	const res = await fetch(url, {
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${process.env.STRAPI_DEV_API_TOKEN}`,
		},
		next: {
			tags: cacheTags,
			revalidate: CacheLength.LONG,
		},
		...options,
	}).catch((error) => {
		// Make some helpful logs, then rethrow
		let errorLog = `Fetch threw an error in _singleStrapiFetch for ${path}. Something is critically wrong and unrecoverable`;
		if (error?.cause?.code === "ECONNREFUSED") {
			errorLog +=
				"\nThe error is ECONNREFUSED. Make sure that strapi is running and that your nextjs/.env is set up correctly to connect to it.";
		}
		log.error(
			{
				module: "app/cms/fetchFromStrapi.ts",
				fn: "_singleStrapiFetch",
				label: "StrapiFetchError",
				url,
				error: makeLoggableError(error),
			},
			errorLog
		);

		throw error;
	});

	const strapiReturn = (await res.json()) as StrapiReturn;

	if (!strapiReturn) {
		// Throw if nothing at all is retrieved
		throw new Error(
			"Strapi returned nothing in requestFromStrapiApi. Something is likely critically wrong"
		);
	}

	if ("error" in strapiReturn) {
		let unrecoverableError = false;
		let errorLog = `Strapi returned an error in requestFromStrapiApi. The calling path is ${path} and resolved to url ${url}.`;
		if (strapiReturn?.error?.status === 404) {
			// Make specific error explanation for 404 error
			errorLog += `\nThe error is a 404. Check that the path in fetchFromStrapi is correct.`;

			// bail if page not found as page is unlikely? to be found?
			unrecoverableError = true;
		} else if (Math.floor(strapiReturn?.error?.status / 100) === 4) {
			// All 400 errors are unrecoverable
			errorLog += `\nThe error is a ${strapiReturn?.error?.status}. The calling path is ${path} and resolved to url ${url}.`;
			unrecoverableError = true;
		}

		// Throw error and log for debugging
		log.debug(
			{
				module: "app/cms/fetchFromStrapi.ts",
				fn: "_singleStrapiFetch",
				label: "StrapiFetchReturnError",
				url,
				strapiReturn,
			},
			errorLog
		);

		if (unrecoverableError) {
			return strapiReturn;
		}

		throw new Error("Strapi returned an error in requestFromStrapiApi");
	}

	return strapiReturn;
};

/**
 * After fetching page 1 of strapi collection, fetches the remaining data and puts it all in one object
 */
const fetchRemainingPagesFromStrapi = async (
	path: string,
	{
		firstPage,
		deep = true,
		params = {},
		fallback = undefined,
		apiType,
		...options
	}: FetchRemainingPagesFromStrapiOptions
) => {
	// The first page should already be guaranteed to be a collection type but double check just in case
	const pagesOnStrapi = firstPage?.meta?.pagination?.pageCount ?? 1;

	// Make array of promises to fetch remaining pages in parallel
	const strapiPageFetches = [];
	for (let pageNumber = 2; pageNumber <= pagesOnStrapi; pageNumber++) {
		const strapiPageFetch = _singleStrapiFetch(path, {
			page: pageNumber,
			deep,
			params,
			fallback,
			apiType,
			...options,
		}) as Promise<StrapiCollectionReturn>;
		strapiPageFetches.push(strapiPageFetch);
	}

	const strapiPageResults = await Promise.all(strapiPageFetches);

	// Uses fallback if *any* page fails to fetch
	for (const strapiPageResult of strapiPageResults) {
		if (!strapiPageResult || "error" in strapiPageResult) {
			return fallback !== undefined ? fallback : strapiPageResult;
		}
	}

	const allPages = firstPage;
	const pageResultsData = (strapiPageResults as StrapiSuccessCollection[]).map(
		(page) => page.data
	);
	allPages.data = firstPage.data.concat(...pageResultsData);

	return allPages;
};

/**
 * Fetches all data from strapi found at a given path
 */
const fetchFromStrapi = async (
	path: string,
	{
		deep = false,
		params = {},
		fallback = undefined,
		apiType,
		...options
	}: FetchFromStrapiOptions = {}
) => {
	const startTime = performance.now();
	let strapiFirstPage: StrapiError | StrapiSuccessSingle;
	try {
		strapiFirstPage = await _singleStrapiFetch(path, {
			deep,
			params,
			apiType,
			...options,
		});
	} catch (error) {
		if (fallback !== undefined) {
			return fallback;
		}
		throw error;
	}

	// Handle error in first page's fetch
	if (!strapiFirstPage || "error" in strapiFirstPage) {
		return fallback !== undefined ? fallback : strapiFirstPage;
	}

	if (!("pagination" in strapiFirstPage.meta)) {
		// This is a singular type, not a collection. Don't bother with pagination
		const endTime = performance.now();
		const timeTaken = endTime - startTime;
		log.debug(
			{
				module: "app/cms/fetchFromStrapi.ts",
				fn: "fetchFromStrapi",
				label: "FetchFromStrapiTiming",
				path,
				timeTaken,
				params,
				options,
				deep,
				pages: 1,
				size: JSON.stringify(strapiFirstPage).length,
			},
			`fetchFromStrapi took ${timeTaken} ms for path ${path}`
		);
		return strapiFirstPage;
	}

	// Handle fetching multiple pages of results
	const strapiAllPagesResult = await fetchRemainingPagesFromStrapi(path, {
		firstPage: strapiFirstPage as StrapiSuccessCollection,
		deep,
		params,
		fallback,
		apiType,
		...options,
	});

	const endTime = performance.now();
	const timeTaken = endTime - startTime;
	log.debug(
		{
			module: "app/cms/fetchFromStrapi.ts",
			fn: "fetchFromStrapi",
			label: "FetchFromStrapiTiming",
			path,
			timeTaken,
			params,
			options,
			deep,
			pages: (strapiFirstPage as StrapiSuccessCollection)?.meta?.pagination
				?.pageCount,
			size: JSON.stringify(strapiAllPagesResult).length,
		},
		`fetchFromStrapi took ${timeTaken} ms for path ${path}`
	);

	return strapiAllPagesResult;
};

export default fetchFromStrapi;
